import { useCallback } from 'react';
import libConstants from '../../constants';
import computeClassName from '../../../../../utils/computeClassName';
import { getValidProp } from '../../../../../utils/cleanValidateStringPropValue';
import handleClick from '../../../../../utils/handleClick';
import handleEventClick from "../../../../../utils/handleEventClick";

const SIZE_LARGE = 'large';
const SIZE_MEDIUM = 'medium';
const SIZE_SMALL = 'small';

const constants = Object.freeze({
  COMPONENT_NAME: 'CCFormInputCheckbox',
  SIZE_LARGE: SIZE_LARGE,
  SIZE_MEDIUM: SIZE_MEDIUM,
  SIZE_SMALL: SIZE_SMALL,
  SIZES: [SIZE_SMALL, SIZE_MEDIUM, SIZE_LARGE],
  SIZE_DEFAULT: SIZE_MEDIUM,

  CLASSNAME_BASE: 'flex-row items-center font-normal',
  CLASSNAME_BASE_IS_INLINE: {
    true: 'inline-flex',
    false: 'flex'
  },

  CLASSNAME_INPUT_BASE: 'appearance-none transition-cc-colors ease-in-out duration-100 border focus:outline-none focus-visible:outline-none',
  CLASSNAME_INPUT_SIZE: Object.fromEntries(
    new Map(
      [
        [SIZE_SMALL, { BASE: 'w-3 h-3 rounded-sm', HAS_LABEL_AND_CAPTION: { true: 'self-start', false: ''}}],
        [SIZE_MEDIUM, { BASE: 'w-4 h-4 rounded', HAS_LABEL_AND_CAPTION: { true: 'self-start mt-0.5', false: ''}}],
        [SIZE_LARGE, { BASE: 'w-5 h-5 rounded', HAS_LABEL_AND_CAPTION: { true: 'self-start mt-0.5', false: ''}}],
      ]
    )
  ),

  CLASSNAME_INPUT_STATES: {
    IS_DISABLED: {
      true: {
        IS_CHECKED_OR_INDETERMINATE: {
          true: {
            HAS_ERROR: {
              true: 'bg-cc-alert-light text-cc-alert-contrast border-transparent',
              false: 'bg-cc-muted-contrast text-cc-white border-transparent'
            }
          },
          false: {
            HAS_ERROR: {
              true: 'bg-cc-alert-light border-transparent',
              false: 'bg-cc-muted border-cc-gray-40'
            }
          }
        }
      },
      false: {
        IS_CHECKED_OR_INDETERMINATE: {
          true: {
            HAS_ERROR: {
              true: 'bg-cc-alert text-cc-alert-contrast border-transparent focus:shadow-pf-focus cursor-pointer',
              false: 'bg-cc-accent text-cc-accent-contrast border-transparent focus:shadow-pf-focus cursor-pointer'
            }
          },
          false: {
            HAS_ERROR: {
              true: 'bg-cc-alert-contrast border-cc-alert focus:shadow-pf-focus cursor-pointer',
              false: 'bg-cc-white hover:bg-cc-accent-light border-cc-gray-40 hover:border-cc-accent focus:shadow-pf-focus cursor-pointer'
            }
          }
        }
      }
    }
  },
  CLASSNAME_INPUT_BG_BASE: 'bg-center bg-no-repeat',
  CLASSNAME_INPUT_BG_CHECKED: Object.fromEntries(
    new Map(
      [
        [SIZE_SMALL, 'bg-cc-icon-check-small'],
        [SIZE_MEDIUM, 'bg-cc-icon-check-medium'],
        [SIZE_LARGE, 'bg-cc-icon-check-large']
      ]
    )
  ),
  CLASSNAME_INPUT_BG_INDETERMINATE: Object.fromEntries(
    new Map(
      [
        [SIZE_SMALL,'bg-cc-icon-indeterminate-small'],
        [SIZE_MEDIUM, 'bg-cc-icon-indeterminate-medium'],
        [SIZE_LARGE, 'bg-cc-icon-indeterminate-large']
      ]
    )
  ),

  CLASSNAME_LABEL_TEXT_BASE: '',
  CLASSNAME_LABEL_TEXT_SIZE: Object.fromEntries(
    new Map(
      [
        [SIZE_SMALL, 'text-cc-body-small ml-2'],
        [SIZE_MEDIUM, 'text-cc-body-medium ml-2'],
        [SIZE_LARGE, 'text-cc-body-large ml-4']
      ]
    )
  ),
  CLASSNAME_LABEL_TEXT_COLOR: {
    IS_DISABLED: {
      true: 'text-cc-muted-contrast',
      false: 'text-cc-primary-contrast'
    }
  },

  CLASSNAME_CAPTION_TEXT_BASE: 'text-cc-body-small',
  CLASSNAME_CAPTION_TEXT_SIZE: Object.fromEntries(
    new Map(
      [
        [SIZE_SMALL, 'mt-1 ml-2'],
        [SIZE_MEDIUM, 'mt-2 ml-2'],
        [SIZE_LARGE, 'mt-2 ml-4']
      ]
    )
  ),
  CLASSNAME_CAPTION_TEXT_COLOR: {
    IS_DISABLED: {
      true: {
        HAS_ERROR: {
          true: 'text-cc-alert-light',
          false: 'text-cc-muted-contrast'
        }
      },
      false: {
        HAS_ERROR: {
          true: 'text-cc-alert',
          false: 'text-cc-secondary-contrast'
        }
      }
    }
  }
});

const propsDefaults = Object.freeze({
  isInline: false,
  isChecked: false,
  isIndeterminate: false,
  isDisabled: false,
  hasError: false,
  checkTitle: 'Select item',
  checkFn: null,
  checkFnArgs: undefined,
  uncheckTitle: 'Deselect item',
  uncheckFn: null,
  uncheckFnArgs: undefined,
  stopEventPropagation: false,
  size: constants.SIZE_DEFAULT
});

function getValidSize(propsSize) {
  return getValidProp(
    propsSize,
    'size',
    constants.SIZE_DEFAULT,
    constants.SIZES,
    constants.COMPONENT_NAME,
    libConstants.LIB_DEBUG_LOGGER,
  );
}

function getComputedClassNameInput(size, isDisabled, isChecked, isIndeterminate, hasError, hasLabelAndCaption) {
  const isCheckedOrIndeterminate = isChecked || isIndeterminate;
  let className = `${constants.CLASSNAME_INPUT_BASE} ${constants.CLASSNAME_INPUT_SIZE[size].BASE} ${constants.CLASSNAME_INPUT_SIZE[size].HAS_LABEL_AND_CAPTION[hasLabelAndCaption]} ${constants.CLASSNAME_INPUT_STATES.IS_DISABLED[isDisabled].IS_CHECKED_OR_INDETERMINATE[isCheckedOrIndeterminate].HAS_ERROR[hasError]}`.trim();
  if (isChecked) {
    return `${className} ${constants.CLASSNAME_INPUT_BG_BASE} ${constants.CLASSNAME_INPUT_BG_CHECKED[size]}`;
  }
  else if (isIndeterminate) {
    return `${className} ${constants.CLASSNAME_INPUT_BG_BASE} ${constants.CLASSNAME_INPUT_BG_INDETERMINATE[size]}`;
  }
  return className;
}

function getComputedClassNameLabelText(size, isDisabled) {
  return `${constants.CLASSNAME_LABEL_TEXT_BASE} ${constants.CLASSNAME_LABEL_TEXT_SIZE[size]} ${constants.CLASSNAME_LABEL_TEXT_COLOR.IS_DISABLED[isDisabled]}`;
}

function getComputedClassNameCaptionText(size, isDisabled, hasError) {
  return `${constants.CLASSNAME_CAPTION_TEXT_BASE} ${constants.CLASSNAME_CAPTION_TEXT_SIZE[size]} ${constants.CLASSNAME_CAPTION_TEXT_COLOR.IS_DISABLED[isDisabled].HAS_ERROR[hasError]}`;
}

function handleChange() { return (event) => {} }

export default function CCFormCheckbox(
  {
    id,
    name,
    value,
    labelText,
    captionText,
    isInline = propsDefaults.isInline,
    isChecked = propsDefaults.isChecked,
    isIndeterminate = propsDefaults.isIndeterminate,
    isDisabled = propsDefaults.isDisabled,
    hasError = propsDefaults.hasError,
    checkTitle = propsDefaults.checkTitle,
    checkFn = propsDefaults.checkFn,
    checkFnArgs = propsDefaults.checkFnArgs,
    uncheckTitle = propsDefaults.uncheckTitle,
    uncheckFn = propsDefaults.uncheckFn,
    uncheckFnArgs = propsDefaults.uncheckFnArgs,
    stopEventPropagation = propsDefaults.stopEventPropagation,
    size = propsDefaults.size,
    className = ''
}) {
  const inputDOMElement = useCallback(
    node => {
      if (node !== null) {
        node.indeterminate = isIndeterminate;
      }
    },
    [isIndeterminate]
  );
  const validSize = getValidSize(size);
  const computedClassName = computeClassName(className, `${constants.CLASSNAME_BASE} ${constants.CLASSNAME_BASE_IS_INLINE[isInline]}`);
  const hasLabelAndCaption = !!labelText&&!!captionText;
  const computedClassNameInput = getComputedClassNameInput(validSize, isDisabled, isChecked, isIndeterminate, hasError, hasLabelAndCaption);
  const computedClassNameLabelText = getComputedClassNameLabelText(validSize, isDisabled);
  const computedClassNameCaptionText = getComputedClassNameCaptionText(validSize,isDisabled,hasError);

  let dynamicProps = {};
  dynamicProps['onChange'] = handleChange();
  if (!!id && !!id.length) dynamicProps['id'] = id;
  if (!!name && !!name.length) dynamicProps['name'] = name;
  if (!!value) dynamicProps['value'] = value;
  if (!isDisabled) {
    const checkUncheckTitle = isChecked?uncheckTitle:checkTitle;
    const checkUncheckFn = isChecked?uncheckFn:checkFn;
    const checkUncheckFnArgs = isChecked?uncheckFnArgs:checkFnArgs;
    if (!!checkUncheckFn && ('function' === typeof checkUncheckFn)) {
      dynamicProps['onClick'] = (event) => {
        if(!!event) {
          if (stopEventPropagation) {
            event.stopPropagation();
          }
        }
        if(undefined === checkUncheckFnArgs) {
          checkUncheckFn();
        }
        else {
          checkUncheckFn(checkUncheckFnArgs);
        }
      }
      if (!!checkUncheckTitle) {
        dynamicProps['title'] = checkUncheckTitle;
      }
    }
  }
  else {
    dynamicProps['disabled'] = true;
  }

  return (
    <label className={computedClassName}>
      <input type="checkbox"
             ref={inputDOMElement}
             {...dynamicProps}
             checked={isChecked}
             className={computedClassNameInput}/>
      {
        !!labelText && !captionText &&
        <span className={computedClassNameLabelText}>{labelText}</span>
      }
      {
        hasLabelAndCaption &&
        (
          <span className="w-full flex flex-col">
            <span className={computedClassNameLabelText}>{labelText}</span>
            <span className={computedClassNameCaptionText}>{captionText}</span>
          </span>
        )
      }
    </label>
  );
}
